import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Users, Calculator, ChevronRight, ChevronDown, Heart, Smile, Eye, Activity, Shield, User, Baby } from "lucide-react";
import { PLAN_TYPE_OPTIONS } from "@shared/schema";
import type { QuotePremiumSummary, PlanPremiumBreakdown } from "@/hooks/useQuotePremiumCalculation";
import { cn, formatDateDisplay, parseDateLocal } from "@/lib/utils";

type QuotePremiumSummaryDisplayProps = {
  premiumSummary: QuotePremiumSummary;
  // Same shape as premiumSummary, computed for the current plan(s) — shown
  // first in each coverage-type group, clearly tagged, so this table doesn't
  // leave the client-facing summary silent on what's being compared against
  // (compact mode only; the non-compact RFP-style layout has no current-plan
  // concept and is left as-is).
  currentPremiumSummary?: QuotePremiumSummary;
  compact?: boolean;
  showEmployerSplit?: boolean;
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const getCoverageLabel = (value: string) =>
  PLAN_TYPE_OPTIONS.find((o) => o.value === value)?.label ||
  value.charAt(0).toUpperCase() + value.slice(1);

const getCoverageIcon = (coverageType: string) => {
  const type = coverageType.toLowerCase();
  switch (type) {
    case 'health':
    case 'medical':
      return Heart;
    case 'dental':
      return Smile;
    case 'vision':
      return Eye;
    case 'life':
      return Activity;
    case 'disability':
    case 'voluntary':
      return Shield;
    default:
      return Shield;
  }
};

export function QuotePremiumSummaryDisplay({
  premiumSummary,
  currentPremiumSummary,
  compact = false,
  showEmployerSplit = true
}: QuotePremiumSummaryDisplayProps) {
  if (premiumSummary.loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Calculating Premiums...
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (premiumSummary.error) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Premium Calculation Error</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{premiumSummary.error}</p>
        </CardContent>
      </Card>
    );
  }

  if (premiumSummary.planBreakdowns.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <Calculator className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>No plans selected for premium calculation</p>
          {premiumSummary.error && (
            <p className="text-xs text-destructive mt-2">Error: {premiumSummary.error}</p>
          )}
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    // Metal tier sort order
    const metalTierOrder: Record<string, number> = {
      'Platinum': 1,
      'Gold': 2,
      'Silver': 3,
      'Bronze': 4,
      'Minimum Coverage': 5,
      'Catastrophic': 6,
    };
    const getMetalTierSortOrder = (tier: string | null | undefined): number => {
      return tier ? (metalTierOrder[tier] || 99) : 99;
    };

    // Group plans by coverage type
    const plansByCoverage: Record<string, PlanPremiumBreakdown[]> = {};
    premiumSummary.planBreakdowns.forEach((breakdown) => {
      const type = breakdown.coverageType;
      if (!plansByCoverage[type]) {
        plansByCoverage[type] = [];
      }
      plansByCoverage[type].push(breakdown);
    });

    // One current plan per coverage type — may cover a type with no renewal
    // options picked yet, so the type list below is the union of both, not
    // just plansByCoverage's keys.
    const currentByCoverage = new Map<string, PlanPremiumBreakdown>();
    (currentPremiumSummary?.planBreakdowns || []).forEach((breakdown) => {
      currentByCoverage.set(breakdown.coverageType, breakdown);
    });
    const coverageTypes = Array.from(
      new Set([...Object.keys(plansByCoverage), ...Array.from(currentByCoverage.keys())]),
    );

    return (
      <div className="space-y-4">
        {coverageTypes.map((coverageType) => {
          const plans = plansByCoverage[coverageType] || [];
          const currentBreakdown = currentByCoverage.get(coverageType);
          const Icon = getCoverageIcon(coverageType);

          // Group plans by carrier within this coverage type
          const plansByCarrier: Record<string, PlanPremiumBreakdown[]> = {};
          plans.forEach((breakdown) => {
            const carrier = breakdown.plan.carrier || (breakdown.plan as any).carrierName || "Unknown Carrier";
            if (!plansByCarrier[carrier]) {
              plansByCarrier[carrier] = [];
            }
            plansByCarrier[carrier].push(breakdown);
          });
          
          // Sort carriers alphabetically
          const sortedCarriers = Object.keys(plansByCarrier).sort();
          
          // Sort plans within each carrier by metal tier, then by name
          sortedCarriers.forEach((carrier) => {
            plansByCarrier[carrier].sort((a, b) => {
              const tierA = getMetalTierSortOrder(a.plan.metalTier);
              const tierB = getMetalTierSortOrder(b.plan.metalTier);
              if (tierA !== tierB) return tierA - tierB;
              return (a.plan.name || '').localeCompare(b.plan.name || '');
            });
          });

          return (
            <Card key={coverageType} className="border-l-4 border-primary">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Icon className="h-5 w-5 text-primary" />
                  {getCoverageLabel(coverageType)}
                  <Badge variant="secondary" className="ml-auto">
                    {plans.length} option{plans.length !== 1 ? 's' : ''}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {currentBreakdown && (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-amber-800 dark:text-amber-300 border-b border-amber-200 dark:border-amber-900 pb-1">
                      Current Plan
                    </div>
                    <CompactPlanCard
                      breakdown={currentBreakdown}
                      showEmployerSplit={showEmployerSplit}
                      isCurrent
                    />
                  </div>
                )}
                {sortedCarriers.map((carrier) => (
                  <div key={carrier} className="space-y-2">
                    <div className="text-sm font-semibold text-primary border-b pb-1">
                      {carrier}
                    </div>
                    {plansByCarrier[carrier].map((breakdown) => (
                      <CompactPlanCard
                        key={breakdown.plan.id}
                        breakdown={breakdown}
                        showEmployerSplit={showEmployerSplit}
                      />
                    ))}
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Plan Options - Each shows individual premium */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            Plan Options & Premiums
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Each plan option shows the total cost for all selected employees. The client will choose one plan from these options.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {premiumSummary.planBreakdowns.map((breakdown, idx) => (
              <PlanBreakdownRow
                key={`${breakdown.plan.id}-${idx}`}
                breakdown={breakdown}
                showEmployerSplit={showEmployerSplit}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Helper to sort dependents alphabetically
const sortDependentsAlphabetically = <T extends { lastName?: string | null; firstName?: string | null }>(deps: T[]): T[] => {
  return [...deps].sort((a, b) => {
    const lastA = (a.lastName || '').toLowerCase();
    const lastB = (b.lastName || '').toLowerCase();
    if (lastA !== lastB) return lastA.localeCompare(lastB);
    const firstA = (a.firstName || '').toLowerCase();
    const firstB = (b.firstName || '').toLowerCase();
    return firstA.localeCompare(firstB);
  });
};

const getMemberIcon = (memberType: string) => {
  switch (memberType) {
    case "spouse":
      return <Users className="h-4 w-4" />;
    case "child":
      return <Baby className="h-4 w-4" />;
    default:
      return <User className="h-4 w-4" />;
  }
};

const getMemberLabel = (memberType: string) => {
  switch (memberType) {
    case "spouse":
      return "Spouse";
    case "child":
      return "Child";
    default:
      return "Employee";
  }
};

function EmployeeInlineBreakdown({
  enrollment,
  breakdown,
}: {
  enrollment: PlanPremiumBreakdown["enrollments"][number];
  breakdown: PlanPremiumBreakdown;
}) {
  const { employee, calculation } = enrollment;
  const sortedDependents = sortDependentsAlphabetically(enrollment.dependents);

  return (
    <div className="space-y-3 py-2">
      {/* Summary row */}
      <div className="flex items-center gap-4 text-sm">
        <Badge variant="secondary">
          {calculation.coverageTier.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
        </Badge>
        <div className="flex gap-4 ml-auto">
          <div><span className="text-muted-foreground">Total:</span> <span className="font-semibold">{formatCurrency(calculation.totalMonthlyRate)}</span></div>
          <div><span className="text-muted-foreground">EE:</span> <span className="font-medium text-blue-600">{formatCurrency(calculation.employeePremium)}</span></div>
          <div><span className="text-muted-foreground">ER:</span> <span className="font-medium text-green-600">{formatCurrency(calculation.employerPremium)}</span></div>
        </div>
      </div>

      {/* Per-member breakdown */}
      <div className="space-y-1.5 ml-4 border-l-2 border-muted-foreground/20 pl-3">
        {calculation.members.map((member, idx) => {
          let memberName = "";
          let memberDob: string | null = null;

          if (member.memberType === "employee") {
            memberName = `${employee.firstName} ${employee.lastName}`;
            memberDob = employee.dateOfBirth ? formatDateDisplay(employee.dateOfBirth) : null;
          } else if (member.memberType === "spouse") {
            const spouse = sortedDependents.find(d => d.relationship.toLowerCase() === "spouse");
            memberName = spouse ? `${spouse.firstName} ${spouse.lastName}` : "Spouse";
            memberDob = spouse?.dateOfBirth ? parseDateLocal(spouse.dateOfBirth)?.toLocaleDateString() ?? null : null;
          } else {
            const children = sortedDependents.filter(d => d.relationship.toLowerCase().startsWith("child"));
            const childIndex = calculation.members.slice(0, idx).filter(m => m.memberType === "child").length;
            const child = children[childIndex];
            memberName = child ? `${child.firstName} ${child.lastName}` : `Child ${childIndex + 1}`;
            memberDob = child?.dateOfBirth ? parseDateLocal(child.dateOfBirth)?.toLocaleDateString() ?? null : null;
          }

          return (
            <div key={idx} className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/30 hover:bg-muted/50">
              <div className="flex items-center gap-2">
                <div className="text-muted-foreground">{getMemberIcon(member.memberType)}</div>
                <div>
                  <div className="text-sm font-medium">{memberName}</div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span>{getMemberLabel(member.memberType)}</span>
                    {member.age !== null && member.age !== undefined && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0">Age {member.age}</Badge>
                    )}
                    {member.ageBandLabel && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0">Band: {member.ageBandLabel}</Badge>
                    )}
                    {memberDob && <span>• DOB: {memberDob}</span>}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold">{formatCurrency(member.monthlyRate)}</div>
                <div className="text-[10px] text-muted-foreground">
                  EE: <span className="text-blue-600">{formatCurrency(member.employeeAmount)}</span>
                  {" | "}
                  ER: <span className="text-green-600">{formatCurrency(member.employerAmount)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Annual projection */}
      <div className="flex gap-4 text-xs text-muted-foreground pt-1 border-t">
        <div>Annual: <span className="font-medium text-foreground">{formatCurrency(calculation.totalMonthlyRate * 12)}</span></div>
        <div>EE Annual: <span className="font-medium text-blue-600">{formatCurrency(calculation.employeePremium * 12)}</span></div>
        <div>ER Annual: <span className="font-medium text-green-600">{formatCurrency(calculation.employerPremium * 12)}</span></div>
      </div>
    </div>
  );
}

function CompactPlanCard({
  breakdown,
  showEmployerSplit,
  isCurrent = false
}: {
  breakdown: PlanPremiumBreakdown;
  showEmployerSplit: boolean;
  /** What this group already has today, not a renewal option — same amber
   * tagging used in Step 5's plan picker and the generated PDF. */
  isCurrent?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div
        className={cn(
          "rounded-lg transition-colors",
          isCurrent
            ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 hover:bg-amber-100/70 dark:hover:bg-amber-950/50"
            : "bg-muted/30 hover:bg-muted/50",
        )}
      >
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between p-3 text-left">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {isCurrent && (
                  <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200 dark:bg-amber-900 dark:text-amber-200 text-xs shrink-0">
                    CURRENT
                  </Badge>
                )}
                <span className="font-medium truncate">{breakdown.plan.name}</span>
                {breakdown.plan.metalTier && (
                  <Badge variant="outline" className="text-xs shrink-0">{breakdown.plan.metalTier}</Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {breakdown.enrollmentCount} employee{breakdown.enrollmentCount !== 1 ? 's' : ''} - Click to view breakdown
              </div>
            </div>
            <div className="text-right ml-4 flex-shrink-0">
              <div className="text-lg font-bold">{formatCurrency(breakdown.totalMonthlyPremium)}</div>
              <div className="text-xs text-muted-foreground">
                {formatCurrency(breakdown.totalAnnualPremium)}/yr
              </div>
              {showEmployerSplit && (
                <div className="text-xs text-muted-foreground mt-1">
                  EE: {formatCurrency(breakdown.employeePortion)} | ER: {formatCurrency(breakdown.employerPortion)}
                </div>
              )}
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-3 pt-1 border-t border-muted">
            <div className="text-xs font-medium text-muted-foreground mb-2">Premium by Employee</div>
            <Accordion type="multiple" className="w-full">
              {breakdown.enrollments.map((enrollment, idx) => {
                const sortedDependents = sortDependentsAlphabetically(enrollment.dependents);
                return (
                  <AccordionItem key={idx} value={`emp-${idx}`} className="border-b last:border-b-0">
                    <AccordionTrigger className="hover:no-underline py-2 px-3 hover:bg-muted rounded-md [&[data-state=open]]:bg-muted">
                      <div className="flex items-center justify-between w-full pr-2">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <div className="text-left">
                            <div className="text-sm font-medium">
                              {enrollment.employee.firstName} {enrollment.employee.lastName}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {enrollment.calculation.coverageTier.replace(/_/g, " ")}
                              {sortedDependents.length > 0 && (
                                <>
                                  {" • "}
                                  {sortedDependents.map((d, i) => (
                                    <span key={d.id}>
                                      {i > 0 && ", "}
                                      {d.firstName} ({d.relationship === "spouse" ? "S" : "C"})
                                    </span>
                                  ))}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-sm font-semibold">{formatCurrency(enrollment.calculation.totalMonthlyRate)}</div>
                          <div className="text-xs text-muted-foreground">per month</div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <EmployeeInlineBreakdown enrollment={enrollment} breakdown={breakdown} />
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function PlanBreakdownRow({
  breakdown,
  showEmployerSplit
}: {
  breakdown: PlanPremiumBreakdown;
  showEmployerSplit: boolean;
}) {
  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-semibold">{breakdown.plan.name}</div>
          <div className="text-sm text-muted-foreground">
            {breakdown.plan.carrier || (breakdown.plan as any).carrierName || "Unknown Carrier"} • {getCoverageLabel(breakdown.coverageType)}
            {breakdown.plan.metalTier && ` • ${breakdown.plan.metalTier}`}
          </div>
        </div>
        <Badge variant="outline">{breakdown.enrollmentCount} employees</Badge>
      </div>

      <div className="grid grid-cols-2 gap-4 pt-3 border-t">
        <div>
          <div className="text-xs text-muted-foreground mb-1">Monthly Total</div>
          <div className="text-lg font-bold">{formatCurrency(breakdown.totalMonthlyPremium)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Annual Total</div>
          <div className="text-lg font-bold">{formatCurrency(breakdown.totalAnnualPremium)}</div>
        </div>
      </div>

      {showEmployerSplit && (
        <div className="flex justify-between text-sm pt-2 border-t">
          <div>
            <span className="text-muted-foreground">Employees: </span>
            <span className="font-medium">{formatCurrency(breakdown.employeePortion)}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Employer: </span>
            <span className="font-medium">{formatCurrency(breakdown.employerPortion)}</span>
          </div>
        </div>
      )}

      {/* Employee List with Inline Breakdown */}
      {breakdown.enrollmentCount > 0 && (
        <div className="pt-3 border-t">
          <div className="text-xs font-medium text-muted-foreground mb-2">Enrolled Employees</div>
          <Accordion type="multiple" className="w-full">
            {breakdown.enrollments.map((enrollment, idx) => {
              const sortedDependents = sortDependentsAlphabetically(enrollment.dependents);
              return (
                <AccordionItem key={idx} value={`emp-${idx}`} className="border-b last:border-b-0">
                  <AccordionTrigger className="hover:no-underline py-2 px-3 hover:bg-muted rounded-md [&[data-state=open]]:bg-muted">
                    <div className="flex items-center justify-between w-full pr-2">
                      <div className="flex items-center gap-2 text-left">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="text-sm font-medium">
                            {enrollment.employee.firstName} {enrollment.employee.lastName}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {enrollment.calculation.coverageTier.replace(/_/g, " ")}
                            {sortedDependents.length > 0 && (
                              <>
                                {" • "}
                                {sortedDependents.map((d, i) => (
                                  <span key={d.id}>
                                    {i > 0 && ", "}
                                    {d.firstName} ({d.relationship === "spouse" ? "S" : "C"})
                                  </span>
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-sm font-semibold">{formatCurrency(enrollment.calculation.totalMonthlyRate)}</div>
                        <div className="text-xs text-muted-foreground">per month</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <EmployeeInlineBreakdown enrollment={enrollment} breakdown={breakdown} />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}
    </div>
  );
}
